import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Message, Recipients, MessageDetail, MessageSummary, Pageable, MessageDetailUI, MessageStatusDTO} from 'src/app/models';
import {ApiFilter, GetQueryParam} from 'src/app/utilities/service';

export interface RecipientStatusDetail {
    status: string;
    received: string;
    address: string;
    contact_type: string;
    delivery_address_id?: string;
    status_type?: string;
    description?: string;
}

export interface RecipientStatusResponse {
    recipient_name: string;
    recipient_id: string;
    recipient_type: string;
    message_request_id: string;
    statuses: RecipientStatusDetail[];
}

@Injectable({
    providedIn: 'root'
})
export class MessagesService {
    private INIT = 'INIT';
    private INDIVIDUALS = 'INDIVIDUALS';

    constructor(public http: HttpClient) {
    }

    private static getName(recipient) {
        return recipient ? (recipient.super_group || recipient.group || recipient.individual || recipient.device || recipient.address) : 'UNKNOWN';
    }

    private static getStatusCounts(message) {
        let acceptCount = 0;
        let declineCount = 0;

        if (message.recipients !== undefined) {
            message.recipients.forEach(recipient => {
                for (let i = 0; i < recipient.statuses.length; i++) {
                    if (recipient.statuses[i].status.includes('ACCEPT')) {
                        acceptCount++;
                        break;
                    }
                    if (recipient.statuses[i].status.includes('DECLINE')) {
                        declineCount++;
                        break;
                    }
                }
            });
            return {
                processing: message.recipients.reduce((total, recipient) => (recipient.processing ? total + 1 : total), 0) > 0,
                responseRequired: message.recipients.reduce((total, recipient) => (recipient.response_required ? total + 1 : total), 0) > 0,
                acceptedCount: acceptCount,
                declinedCount: declineCount
            };
        } else {
            return {
                processing: false,
                responseRequired: false,
                acceptedCount: 0,
                declinedCount: 0
            };
        }
    }

    sendAlert(message: Message) {
        return this.http.post('api/v1/alert', message);
    }

    sendMessage(message: Message) {
        return this.http.post('/api/v2/messageAsync', message, {
            responseType: 'text',
            observe: 'response'
        });
    }

    getAlertDetailsDataReply(msgId) {
        return this.http.get('api/v1/messages/' + msgId);
    }

    getRecipientCount(recipients: Recipients) {
        return this.http.post('/api/v1/recipients/count',recipients);
    }

    validateTeamsAddress(address: string) {
        return this.http.post('/api/v1/validateAlertingLogicAppAddress', address);
    }

    getMessages(fromDate, toDate, params) {
        // const params = new HttpParams()
        //     .set('page', page.offset.toString())
        //     .set('size', page.limit.toString());

        const apiVar = `api/v1/messages?messageStartDate=${fromDate}&messageEndDate=${toDate}`;

        return this.http.get(apiVar, {params}).pipe(
            map(pageable => this.mapMessage(pageable))
        );
    }

    getMessageSummaryV2CompleteSearch(filter: ApiFilter): Observable<Pageable<MessageSummary>> {
        return this.http.get<Pageable<MessageSummary>>(`api/v2/message/summary?${GetQueryParam(filter)}`);
    }

    getMessageDetailV2(messageRequestId: string): Observable<MessageDetail> {
        return this.http.get<MessageDetail>(`api/v2/message/detail/${messageRequestId}`);
    }

    getRecipientStatusV2(messageRequestId: string, recipientIdentifier: string, recipientType?: string): Observable<RecipientStatusResponse> {
        let url = `api/v2/message/${messageRequestId}/recipient-status/${encodeURIComponent(recipientIdentifier)}`;
        if (recipientType) {
            url += `?recipient_type=${recipientType}`;
        }
        return this.http.get<RecipientStatusResponse>(url);
    }

    getMessagesFilter(page, offset, fromDate, toDate, messageText, from, to, groupType, requiresAcknowledgement, showMessagesNOTFromSVC) {
        const params = new HttpParams()
            .set('sort', `${page.orderBy},${page.orderDir}`)
            .set('page', offset.toString())
            .set('size', page.limit.toString());
        const apiVar = `api/v1/messages?messageStartDate=${fromDate}&messageEndDate=${toDate}&message=${messageText}&from=${from}&to=${to}&groupType=${groupType}&state=${requiresAcknowledgement}&notFromSVC=${showMessagesNOTFromSVC}`;

        return this.http.get(apiVar, {params}).pipe(
            map(pageable => this.mapMessage(pageable))
        );
    }

    mapMessage(pageable) {
        pageable.content.forEach(message => {
            message.acceptedCount = 0;
            message.declinedCount = 0;
            this.formatToName(message);
            if (message.recipients !== undefined) {
                message.recipients.forEach(recipient => {
                    this.setStatus(recipient, null);
                    Object.assign(message, MessagesService.getStatusCounts(message));
                });
            }
        });

        return pageable;
    }

    getTagsByMessage(message_id) {
        return this.http.get('api/v1/message/' + message_id + '/tags');
    }

    getAlertDetailsData(msgId: string): Observable<MessageDetailUI> {
        return this.http.get<MessageStatusDTO>('api/v1/messages/' + msgId).pipe(
            map(messageStatus => this.groupRecipients(messageStatus))
        );
    }

    groupRecipients(messageStatus: MessageStatusDTO): MessageDetailUI {
        let uiFormat: MessageDetailUI = {
            messageId: null,
            sent: null,
            recipientCount: messageStatus.recipients?.length || 0,
            processing: 0,
            acceptedCount: 0,
            declinedCount: 0,
            escalations: [],
            message: null,
            sentBy: null
        };

        uiFormat.messageId = messageStatus.id;
        uiFormat.sent = messageStatus.sent || messageStatus.sendDate || messageStatus.send_date;
        uiFormat.message = messageStatus.message;
        uiFormat.sentBy = messageStatus.from || messageStatus.sentBy || messageStatus.sent_by;

        const escalations = {};
        const superGroups = {};
        const groups = {};

        let escalation_ordinal = 0;
        if (messageStatus.recipients !== undefined) {
            messageStatus.recipients.forEach(recipient => {
                const escalation = this.getEscalation(recipient, escalations, escalation_ordinal++);
                const superGroup = this.getSuperGroup(recipient, superGroups);
                const group = this.getGroup(recipient, groups);
                if (superGroup) {
                    this.pushIfNotExists(superGroup.groups, group);
                    this.pushIfNotExists(escalation.groups, superGroup);
                } else {
                    this.pushIfNotExists(escalation.groups, group);
                }
            });
        }

        for (const escalation_id in escalations) {
            if (Object.prototype.hasOwnProperty.call(escalations, escalation_id)) {
                // SORT _should_ be coming from API.
                uiFormat.escalations.push(escalations[escalation_id]);
            }
        }

        // Set Processing Flag
        this.setProcessingFlag(uiFormat.escalations);

        // Sort uiFormat.escalations by ordinal Desc
        uiFormat.escalations.sort(this.compareEscalations.bind(this));

        Object.assign(uiFormat, MessagesService.getStatusCounts(messageStatus));

        return uiFormat;
    }

    setProcessingFlag(collection) {
        let collectionProcessing = false;

        collection.forEach(item => {
            item.processing = (item.groups)
                ? this.setProcessingFlag(item.groups)
                : item.recipients.reduce((total, recipient) => (recipient.processing ? total + 1 : total), 0) > 0;

            collectionProcessing = collectionProcessing || item.processing;
        });

        return collectionProcessing;
    }

    getMessagePreview(id: number): Observable<Object> {
        return this.http.get(`/api/v1/messages/${id}/preview`);
    }

    getDefaultAlert() {
        return {
            id: null,
            sent: null,
            from: null,
            message: null,
            application_names: null,
            client_names: null,
            template_id: null,
            template_name: null,
            alert: null,
            parent_id: null,
            sent_by: null,
            recipients: [
                {
                    id: null,
                    sent: null,
                    recipient_type: null,
                    recipient_id: null,
                    provider_reference: null,
                    processing: null,
                    response_required: null,
                    response_received: null,
                    decline_reason_id: null,
                    decline_reason: null,
                    super_group_id: null,
                    super_group: null,
                    super_group_device_name: null,
                    group_id: null,
                    group: null,
                    group_device_name: null,
                    contact_id: null,
                    individual_id: null,
                    individual: null,
                    external_id: null,
                    device_id: null,
                    channel_name: null,
                    device_name: null,
                    address: null,
                    address_type: null,
                    escalate_from_id: null,
                    name: null,
                    escalate_from_type: null,
                    escalate_from: null,
                    statuses: null
                }
            ],
            tags: null
        };
    }

    acknowledge(provider_reference, response, decline_reason?: any) {
        let options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'text/plain'
            }),
            responseType: 'text' as 'json'
        };
        return this.http.get(`/api/v1/acknowledge/${provider_reference}?response=${response}${!decline_reason ? '' : `&rfd=${decline_reason}`}`, options);
    }

    acknowledgeV2(messageRequestId: string, response: string, individualId?: number, deviceId?: number, declineReasonId?: string) {
        const body: { response: string; decline_reason_id?: string; individual_id?: number; device_id?: number } = {
            response: response
        };

        if (individualId) {
            body.individual_id = individualId;
        }

        if (deviceId) {
            body.device_id = deviceId;
        }

        if (declineReasonId) {
            body.decline_reason_id = declineReasonId;
        }

        return this.http.post(`/api/v2/acknowledge/${messageRequestId}`, body);
    }

    private formatToName(message) {
        const filteredArr = message.recipients.filter((r, i, a) => a.findIndex(r2 => r2.super_group_id === r.super_group_id) === i
            || (!r.super_group && a.findIndex(r2 => r2.group_id === r.group_id) === i) || !r.group_id);
        const filtered = [...new Set(filteredArr)];

        message['to'] = MessagesService.getName(filtered[0]) + (filtered.length > 1 ? ` and ${filtered.length - 1} more` : '');
    }

    private compareEscalations(escalationA, escalationB) {
        return (escalationA.ordinal < escalationB.ordinal) ? -1 : (escalationA > escalationB) ? 1 : 0;
    }

    private getEscalationId(recipient) {
        return recipient.escalate_from_id || this.INIT;
    }

    private getSuperGroupId(recipient) {
        return [this.getEscalationId(recipient), recipient.super_group_id].join('__');
    }

    private getGroupId(recipient) {
        return [this.getEscalationId(recipient), recipient.super_group_id, recipient.group_id || this.INDIVIDUALS].join('__');
    }

    private pushIfNotExists(array, item) {
        const idx = array.findIndex(i => i.id === item.id);
        if (idx === -1) {
            array.push(item);
        }

    }

    private getEscalation(recipient, escalations, ordinal) {
        const escalation_id = this.getEscalationId(recipient);
        const desc = recipient.escalate_from ? 'Escalating from ' + recipient.escalate_from + ' to ' + MessagesService.getName(recipient) : 'Initial Notification';
        escalations[escalation_id] = escalations[escalation_id] || {ordinal: ordinal, id: escalation_id, name: desc, processing: false, groups: []};
        return escalations[escalation_id];
    }

    private getSuperGroup(recipient, superGroups) {
        if (recipient.super_group_id) {
            const id = this.getSuperGroupId(recipient);
            superGroups[id] = superGroups[id] || {id: id, name: recipient.super_group, processing: false, groups: []};
            return superGroups[id];
        }

        return null;
    }

    private getGroup(recipient, groups) {
        // Group by Group or Individuals
        const id = this.getGroupId(recipient);
        const group_id = recipient.super_group_id;
        const name = recipient.group || 'INDIVIDUALS';
        const latestStatus = (recipient.statuses && recipient.statuses.length > 0) ? recipient.statuses[0].status : '';
        groups[id] = groups[id] || {id: id, group_id: group_id, name: name, processing: false, recipients: []};

        const formattedRecipient = {
            id: recipient.id,
            provider_reference: recipient.provider_reference,
            processing: recipient.processing,
            ack_required: recipient.response_required,
            ackReceived: recipient.response_received,
            decline_reason_id: recipient.decline_reason_id,
            decline_reason: recipient.decline_reason,
            sent: recipient.sent,
            latest_status: latestStatus,
            group_id: recipient.group_id,
            group_name: recipient.group,
            group: recipient.group,
            individualId: recipient.individual_id,
            individualName: recipient.individual_name,
            individual: recipient.individual,
            deviceId: recipient.device_id,
            channel_name: recipient.channel_name,
            address: recipient.address,
            address_type: recipient.address_type,
            accepted: false,
            declined: false,
            statuses: []
        };

        this.setStatus(recipient, formattedRecipient);

        groups[id].recipients.push(formattedRecipient);
        return groups[id];
    }

    private setStatus(recipient, formattedRecipient) {
        recipient.accepted = false;
        recipient.declined = false;

        if (recipient.statuses) {
            recipient.statuses.forEach(status => {
                // Set ACCEPT or DECLINE state based on latest acknowledgement
                if (!(recipient.accepted || recipient.declined)) {
                    if (status.status.toUpperCase().includes('ACCEPT')) {
                        recipient.accepted = true;
                    }
                    if (status.status.toUpperCase().includes('DECLINE')) {
                        recipient.declined = true;
                    }
                }

                if (formattedRecipient) {
                    formattedRecipient.accepted = recipient.accepted;
                    formattedRecipient.declined = recipient.declined;

                    formattedRecipient.statuses.push({
                        id: status.id,
                        status: status.status,
                        acknowledgment_received: status.response_received,
                        received: status.received
                    });
                }
            });
        }
    }
}
